import axios from "axios";

export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

export const rupiah = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

export const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return e?.message || "Terjadi kesalahan";
};

export const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await API.post("/upload", fd);
  return data.url;
};

const LEGACY_ASSET_ROOT = "https://hvyvgsheymwocsskarmq.supabase.co/storage/v1/object/public/nawadah-uploads";
// Serve uploads through the site domain so Cloudflare can cache immutable media
// at the edge instead of charging Supabase Storage for every repeat view.
export const ASSETS = "/media";
export const LOGO_URL = `${ASSETS}/1db42203-3aac-436c-9071-54a41f0d5b3d.png`;
const REPLACED_LOGOS = new Set([
  `${LEGACY_ASSET_ROOT}/678a707e-34ee-4094-af4e-e1bb06e8a7bb.png`,
  `${LEGACY_ASSET_ROOT}/085eddce-7bc6-431e-9582-aae0913b6d4e.jpg`,
  `${LEGACY_ASSET_ROOT}/01d957c3-de80-4d1c-a6e4-ff2cee66e8b8.jpg`,
  `${ASSETS}/678a707e-34ee-4094-af4e-e1bb06e8a7bb.png`,
  `${ASSETS}/085eddce-7bc6-431e-9582-aae0913b6d4e.jpg`,
  `${ASSETS}/01d957c3-de80-4d1c-a6e4-ff2cee66e8b8.jpg`,
  `${LEGACY_ASSET_ROOT}/d35de1de-cf70-466c-9b39-9e124a7b7041.jpg`,
  `${ASSETS}/d35de1de-cf70-466c-9b39-9e124a7b7041.jpg`,
]);
export const IMG_LUMBA = `${ASSETS}/d80e1c2f-0773-4125-ba20-68aa10505378.jpg`;
export const IMG_KUDA = `${ASSETS}/c6e98928-da73-46d5-8586-1275a069a7d8.jpg`;
export const IMG_KATROL = `${ASSETS}/0024cfc9-61d8-4f95-8fb9-4d1855b7b329.jpg`;
export const IMG_TIMBANGAN = `${ASSETS}/60d3ca66-a052-46e8-82a9-9c2a8f0c9698.jpg`;

export const WA_NUMBER = "6285717500023";
export const waLink = (text, num = WA_NUMBER) => `https://wa.me/${num}?text=${encodeURIComponent(text)}`;

export const waNum = (phone) => {
  const p = String(phone || "").replace(/\D/g, "");
  if (!p) return WA_NUMBER;
  return p.startsWith("62") ? p : p.startsWith("0") ? "62" + p.slice(1) : p;
};

export const SECTION_DEFS = [
  { key: "features", label: "Keunggulan / Fitur", visible: true },
  { key: "categories", label: "Kategori Produk", visible: true },
  { key: "brands", label: "Merek Resmi", visible: true },
  { key: "testimonials", label: "Testimoni Pelanggan", visible: true },
  { key: "blog", label: "Blog & Artikel", visible: true },
  { key: "videos", label: "Video", visible: true },
  { key: "cta", label: "Ajakan (CTA)", visible: true },
];

export const mergeHomeSections = (saved = [], defaults = SECTION_DEFS) => {
  if (!saved.length) return defaults.map((item) => ({ ...item }));
  const known = new Map(defaults.map((item) => [item.key, item]));
  const result = saved
    .filter((item) => known.has(item.key))
    .map((item) => ({ ...known.get(item.key), ...item }));
  defaults.forEach((item, defaultIndex) => {
    if (result.some((entry) => entry.key === item.key)) return;
    const nextKey = defaults.slice(defaultIndex + 1).find((candidate) => result.some((entry) => entry.key === candidate.key))?.key;
    const insertAt = nextKey ? result.findIndex((entry) => entry.key === nextKey) : result.length;
    result.splice(insertAt, 0, { ...item });
  });
  return result;
};

export const THEMES = [
  { v: "navy-gold", l: "Navy Emas Klasik", d: "Identitas NAWADAH — navy pekat & emas", c: ["#040F1D", "#0B1F3A", "#C9A227"] },
  { v: "champagne", l: "Champagne Mewah", d: "Gradasi espresso & champagne lembut", c: ["#16110B", "#2C2318", "#D8C08D"] },
  { v: "rose-gold", l: "Rose Gold Lembut", d: "Plum gelap & rose gold kalem", c: ["#181016", "#31212B", "#D9A69B"] },
  { v: "sage", l: "Sage Hijau Tenang", d: "Hijau sage soft & natural", c: ["#11150F", "#243222", "#A9BE9E"] },
  { v: "dusty-blue", l: "Dusty Blue Elegan", d: "Biru abu lembut profesional", c: ["#0F141C", "#1F2D3E", "#93ABC6"] },
  { v: "terracotta", l: "Terracotta Hangat", d: "Tanah liat hangat & kalem", c: ["#171009", "#2F1F12", "#D09A72"] },
  { v: "lavender", l: "Lavender Grey Mewah", d: "Ungu abu soft berkelas", c: ["#131118", "#252134", "#B3AACB"] },
  { v: "bronze", l: "Charcoal Bronze", d: "Arang gelap & perunggu elegan", c: ["#131210", "#28241E", "#BE9E6E"] },
  { v: "img-maritime", l: "Maritim Elegan", d: "Tema gambar — dek kapal & tali, aksen emas", c: ["#040F1D", "#0B1F3A", "#C9A227"], img: `${ASSETS}/785bd11b-8a7f-46a7-9a96-ba278239e153.jpg` },
  { v: "img-emerald", l: "Zamrud Sutra", d: "Tema gambar — kain sutra hijau mewah", c: ["#04150F", "#0B3D2E", "#D4AF37"], img: `${ASSETS}/ec08a6b9-bc75-4246-a259-3b15323a254b.jpg` },
  { v: "img-carbon", l: "Baja Karbon", d: "Tema gambar — logam industrial premium", c: ["#0B0E12", "#27303A", "#F97316"], img: `${ASSETS}/fc939030-db45-41d7-b851-b9ca82b9bd9a.jpg` },
];

export const BG_STYLES = [
  { v: "maritime", l: "Bervariasi (Bawaan)", d: "Setiap bagian punya tekstur berbeda — tampilan bawaan", cls: "" },
  { v: "garis", l: "Garis Halus", d: "Garis diagonal tipis yang tenang", cls: "motif-garis" },
  { v: "titik", l: "Titik Lembut", d: "Polka halus berjarak lebar", cls: "motif-titik" },
  { v: "gelombang", l: "Gelombang Tenang", d: "Lekuk ombak samar yang mengalir", cls: "motif-gelombang" },
  { v: "berlian", l: "Anyaman Berlian", d: "Kisi belah ketupat elegan", cls: "motif-berlian" },
  { v: "herringbone", l: "Herringbone Klasik", d: "Zigzag tulang ikan yang mewah", cls: "motif-herringbone" },
  { v: "anyam", l: "Anyaman Tali", d: "Motif anyaman tali khas NAWADAH", cls: "bg-rope-pattern" },
  { v: "polos", l: "Polos Mewah", d: "Bersih tanpa tekstur sama sekali", cls: "bg-polos-override" },
];

export const LAYOUTS = [
  { v: "classic", l: "Klasik Profesional", d: "Hero besar rata kiri, alur standar", hero: "classic", order: [] },
  { v: "tengah", l: "Fokus Tengah", d: "Teks hero di tengah — simetris & megah", hero: "center", order: [] },
  { v: "belah", l: "Belah Dua Modern", d: "Teks kiri, gambar berbingkai di kanan", hero: "split", order: [] },
  { v: "ringkas", l: "Ringkas & Padat", d: "Hero lebih pendek, konten lebih rapat", hero: "compact", order: [] },
  { v: "katalog", l: "Katalog Dulu", d: "Kategori produk langsung setelah hero", hero: "classic", order: ["categories", "features", "brands", "testimonials", "blog", "videos", "cta"] },
  { v: "merek", l: "Sorotan Merek", d: "Merek unggulan tampil paling awal", hero: "split", order: ["brands", "features", "categories", "testimonials", "blog", "videos", "cta"] },
  { v: "sosial", l: "Bukti Sosial", d: "Testimoni pelanggan ditonjolkan dulu", hero: "center", order: ["testimonials", "features", "categories", "brands", "blog", "videos", "cta"] },
  { v: "edukasi", l: "Edukasi Dulu", d: "Artikel & panduan tampil lebih awal", hero: "classic", order: ["blog", "videos", "features", "categories", "brands", "testimonials", "cta"] },
];

export const BLOG_LAYOUTS = [
  { v: "grid", l: "Grid Kartu", d: "2 kolom kartu sejajar" },
  { v: "list", l: "Daftar Editorial", d: "Baris horizontal seperti majalah" },
  { v: "unggulan", l: "Sorotan Unggulan", d: "Artikel pertama besar, sisanya grid" },
];

export const BLOG_DETAIL_LAYOUTS = [
  { v: "klasik", l: "Klasik", d: "Judul, lalu gambar sampul, lalu isi" },
  { v: "sinematik", l: "Sinematik", d: "Gambar besar penuh di atas judul" },
  { v: "minimal", l: "Minimal Fokus", d: "Tanpa gambar besar — fokus ke teks" },
];

export const PAYMENT_METHODS = {
  cash: { label: "Cash / Tunai", color: "bg-emerald-100 text-emerald-800" },
  transfer: { label: "Transfer Bank", color: "bg-blue-100 text-blue-800" },
  qris: { label: "QRIS", color: "bg-purple-100 text-purple-800" },
  hutang: { label: "Hutang (Tempo)", color: "bg-amber-100 text-amber-800" },
};

export const logoSrc = (url) => (!url || REPLACED_LOGOS.has(url) ? LOGO_URL : imgSrc(url));
export const siteLogo = (s) => logoSrc(s?.logo || s?.logo_url);
export const adminLogo = (s) => logoSrc(s?.admin_logo || s?.logo || s?.logo_url);
export const waList = (s) => {
  const arr = (s?.whatsapp_numbers || []).filter((w) => w && w.number);
  return arr.length
    ? arr.map((w, index) => ({ ...w, label: w.label || `Admin Nawadah ${index + 1}`, image: w.image || "" }))
    : [{ label: "Admin Nawadah 1", number: s?.whatsapp || "0857-1750-0023", image: "" }];
};
export const igList = (s) => {
  const arr = (s?.instagrams || []).filter((g) => g && g.handle);
  return arr.length ? arr : [{ label: "Instagram", handle: s?.instagram || "@nawadah.official" }];
};
export const addrList = (s) => (s?.addresses || []).filter((a) => a && a.address);
export const bankList = (s) => {
  const arr = (s?.bank_accounts || []).filter((b) => b && b.account_number);
  if (arr.length) return arr;
  return s?.bank_account ? [{ bank_name: s.bank_name, account_number: s.bank_account, holder: s.bank_holder }] : [];
};
export const hoursList = (s) => {
  const arr = (s?.operating_hours_list || []).filter((item) => item && item.hours);
  return arr.length ? arr : [{ label: "", hours: s?.operating_hours || "Senin - Sabtu, 08.00 - 16.00 WIB" }];
};

export const normalizeBusinessSettings = (value = {}) => {
  const settings = { ...value };
  if (!Array.isArray(settings.bank_accounts) || !settings.bank_accounts.length) {
    settings.bank_accounts = settings.bank_name || settings.bank_account || settings.bank_holder
      ? [{ bank_name: settings.bank_name || "", account_number: settings.bank_account || "", holder: settings.bank_holder || "" }]
      : [];
  }
  if (!Array.isArray(settings.whatsapp_numbers) || !settings.whatsapp_numbers.length) {
    const numbers = String(settings.whatsapp || "")
      .split(/\s+(?:dan|atau)\s+|[,;/]+/i)
      .map((number) => number.trim())
      .filter(Boolean);
    settings.whatsapp_numbers = numbers.map((number, index) => ({ label: `Admin Nawadah ${index + 1}`, number, image: "" }));
  }
  if (!Array.isArray(settings.operating_hours_list) || !settings.operating_hours_list.length) {
    settings.operating_hours_list = settings.operating_hours
      ? [{ label: "", hours: settings.operating_hours }]
      : [];
  }
  return settings;
};

export const backgroundMusicTracks = (settings = {}) => {
  const source = settings || {};
  const configured = Array.isArray(source.background_music_playlist)
    ? source.background_music_playlist
    : [];
  const playlist = configured
    .map((track, index) => {
      const url = String(track?.url || "").trim();
      const title = String(track?.title || `Musik ${index + 1}`).trim() || `Musik ${index + 1}`;
      return {
        id: String(track?.id || `music-${index + 1}`),
        title,
        url,
      };
    })
    .filter((track) => track.url);

  if (playlist.length) return playlist;

  const legacyUrl = String(source.background_music_url || "").trim();
  if (!legacyUrl) return [];
  return [{
    id: "music-legacy",
    title: String(source.background_music_title || "Musik NAWADAH").trim() || "Musik NAWADAH",
    url: legacyUrl,
  }];
};

export const prepareBusinessSettings = (value = {}) => {
  const normalized = normalizeBusinessSettings(value);
  const musicTracks = backgroundMusicTracks(value);
  const primaryTrack = musicTracks[0] || {};
  const settings = {
    ...normalized,
    bank_accounts: Array.isArray(value.bank_accounts) ? value.bank_accounts : normalized.bank_accounts,
    whatsapp_numbers: Array.isArray(value.whatsapp_numbers) ? value.whatsapp_numbers : normalized.whatsapp_numbers,
    operating_hours_list: Array.isArray(value.operating_hours_list) ? value.operating_hours_list : normalized.operating_hours_list,
    background_music_playlist: musicTracks,
    // Retain the original fields for recovery builds that only understand one song.
    background_music_url: primaryTrack.url || "",
    background_music_title: primaryTrack.title || "",
  };
  const banks = settings.bank_accounts || [];
  const whatsapps = settings.whatsapp_numbers || [];
  const hours = settings.operating_hours_list || [];
  const primaryBank = banks.find((item) => item.account_number || item.bank_name || item.holder) || {};
  const hourText = hours
    .filter((item) => item?.hours)
    .map((item) => item.label ? `${item.label}: ${item.hours}` : item.hours)
    .join(" • ");
  return {
    ...settings,
    bank_name: primaryBank.bank_name || "",
    bank_account: primaryBank.account_number || "",
    bank_holder: primaryBank.holder || "",
    whatsapp: whatsapps.map((item) => item?.number).filter(Boolean).join(" dan "),
    operating_hours: hourText,
  };
};

export const CATEGORIES = [
  { value: "tali", label: "Tali Tambang" },
  { value: "katrol", label: "Katrol" },
  { value: "timbangan", label: "Timbangan" },
];

export const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

export const ORDER_STATUS = {
  menunggu_ongkir: { label: "Menunggu Ongkir", color: "bg-amber-100 text-amber-800" },
  menunggu_pembayaran: { label: "Menunggu Pembayaran", color: "bg-blue-100 text-blue-800" },
  menunggu_konfirmasi: { label: "Konfirmasi Pembayaran", color: "bg-purple-100 text-purple-800" },
  diproses: { label: "Diproses", color: "bg-cyan-100 text-cyan-800" },
  dikirim: { label: "Dikirim", color: "bg-indigo-100 text-indigo-800" },
  selesai: { label: "Selesai", color: "bg-emerald-100 text-emerald-800" },
  dibatalkan: { label: "Dibatalkan", color: "bg-red-100 text-red-800" },
};

export const imgSrc = (url) => {
  if (!url) return "";
  if (url.startsWith(`${LEGACY_ASSET_ROOT}/`)) return `${ASSETS}/${url.slice(LEGACY_ASSET_ROOT.length + 1)}`;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
};

export const backgroundMusicConfig = (settings = {}) => {
  const source = settings || {};
  const tracks = backgroundMusicTracks(source).map((track) => ({ ...track, url: imgSrc(track.url) }));
  const firstTrack = tracks[0] || { title: "Musik NAWADAH", url: "" };
  const rawVolume = Number(source.background_music_volume);
  const volumePercent = Number.isFinite(rawVolume) ? Math.min(100, Math.max(0, rawVolume)) : 24;
  return {
    tracks,
    url: firstTrack.url,
    title: firstTrack.title,
    enabled: tracks.length > 0 && source.background_music_enabled !== false,
    autoplay: source.background_music_autoplay !== false,
    loop: source.background_music_loop !== false,
    volume: volumePercent / 100,
  };
};

export const IMAGE_RATIOS = [
  { value: "1:1", label: "1:1", className: "aspect-square" },
  { value: "4:3", label: "4:3", className: "aspect-[4/3]" },
  { value: "16:9", label: "16:9", className: "aspect-video" },
];

export const normalizeImageRatio = (ratio, fallback = "4:3") =>
  IMAGE_RATIOS.some((item) => item.value === ratio) ? ratio : fallback;

export const imageRatioClass = (ratio, fallback = "4:3") =>
  IMAGE_RATIOS.find((item) => item.value === normalizeImageRatio(ratio, fallback))?.className || "aspect-[4/3]";

export const IMAGE_POSITIONS = [
  { value: "left", label: "Kiri", className: "object-left" },
  { value: "center", label: "Tengah", className: "object-center" },
  { value: "right", label: "Kanan", className: "object-right" },
];

export const normalizeImagePosition = (position, fallback = "center") =>
  IMAGE_POSITIONS.some((item) => item.value === position) ? position : fallback;

export const imagePositionClass = (position, fallback = "center") =>
  IMAGE_POSITIONS.find((item) => item.value === normalizeImagePosition(position, fallback))?.className || "object-center";

export const videoEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) return { type: "youtube", src: `https://www.youtube.com/embed/${m[1]}` };
  return { type: "video", src: imgSrc(url) };
};
